// buildMatchImpactFactsFromInsights.ts
//
// Fully typed against your InsightsJson / InsightsRally / InsightsShot definitions.
// - Computes finalScore internally (prefers scoring_info.running_score where likely_correct_score)
// - Attributes per-player good/bad events with evidence examples
// - Uses errors.faults.{net,kitchen,paddle_hit_net,out} when present
// - Trusts scoring_info.server_number (1..4 => player 0..3)
// - per-team rollups (good/bad buckets, plus top causes and swing rallies)
// - importance weighting per rally (score context) and weighted rollups/sorts
//
// IMPORTANT UPDATE:
// - Build a single ctx object ONCE per rally (after all needed fields exist)
// - Compute endInf ONCE per rally (no scattered infer calls)
// - Every exWithImportance call spreads ctx so examples are self-contained
//
// NEW (pressure additions):
// - Adds "lead gap" pressure (jump-out leads) and "score drought" pressure (no-point runs)
// - Tracks lead persistence across rallies
// - Adds extra context fields into ImpactExample so UI can render heatmaps/timelines/tooltips

import type {
  InsightsJson,
  InsightsRally,
  InsightsShot,
  InsightsFaults,
} from "../../shared/types/insightsEtcTypes"; // <-- adjust path

export type Team01 = 0 | 1;

// Tune these without changing semantics elsewhere
const LEAD_GAP_MIN = 3; // “feels real”
const BIG_LEAD_GAP_MIN = 5; // “whoa”
const LEAD_PERSIST_MIN = 3; // lead gap present for >= this many rallies -> extra pressure

const DROUGHT_MIN = 3; // after 3 no-point rallies in a row it starts to feel “stuck”
const BIG_DROUGHT_MIN = 6;

export type PressureByRally = {
  rallyIndex: number;
  scoreBefore?: [number, number];
  scoreDiff?: number | null;
  leadTeam?: Team01 | null;
  trailingTeam?: Team01 | null;

  leadGapRunLen: number;

  weight: number;
  reasons: ImportanceReason[];

  // drought info (TEAM point drought, not “consecutive sideouts”)
  noPointRunLenBefore: number; // max(team0Drought, team1Drought)
  droughtTeam: 0 | 1 | null;
  droughtByTeam: [number, number];
  droughtByTeamBefore: [number, number];
};

export type ImportanceReason =
  | "no_score_context"
  | "late_game"
  | "close_score"
  | "game_point"
  | "sideout_server_at_game_point"
  // NEW
  | "lead_gap"
  | "big_lead_gap"
  | "lead_has_persisted"
  | "score_drought"
  | "big_score_drought"
  | "break_drought_opportunity"
  | "trailing_team_serving"
  | "leading_team_serving";

type WinnerSource = "rally.winning_team" | "finalShot.playerTeam";

export type ImpactExample = {
  rallyIndex: number;
  shotNumber?: number | null;
  note?: string;
  weight?: number;
  scoreBefore?: [number, number];

  importanceReasons?: ImportanceReason[];

  // NEW: UI-friendly pressure context
  scoreDiff?: number; // abs(a-b)
  leadTeam?: Team01 | null; // who is ahead at scoreBefore (or null if tied/unknown)
  trailingTeam?: Team01 | null;
  noPointRunLenBefore?: number; // consecutive no-point rallies before this rally
  leadGapRunLen?: number; // consecutive rallies where the same leadTeam has lead >= LEAD_GAP_MIN

  // who/what context so you don’t have to hunt
  winnerTeam?: Team01 | null;
  winnerTeamCanonical?: Team01 | null; // = winnerTeamForAttribution ?? winnerTeam?: Team01 | null;
  winnerTeamSource?: WinnerSource;

  serverPlayerId?: 0 | 1 | 2 | 3 | null;
  serverTeam?: Team01 | null;

  finalHitterPlayerId?: 0 | 1 | 2 | 3 | null;
  finalHitterTeam?: Team01 | null;

  shotsInRally?: number;

  // unified “how rally ended”
  endKind?: "winner" | "error" | "unknown";
  faultType?: string | null; // "net" | "out:long" | ...
  faultSource?: "explicit" | "crossed_net" | "tags" | null;
  endedOnReturnShot2?: boolean; // easy UI badge
};

export type ImpactBucket = {
  key: string;
  count: number;
  examples: ImpactExample[];
  weighted?: number;
};

export type ImpactFactsPlayer = {
  playerId: 0 | 1 | 2 | 3;
  team: Team01;
  good: ImpactBucket[];
  bad: ImpactBucket[];
};

export type ImpactFactsTeam = {
  team: Team01;

  good: ImpactBucket[];
  bad: ImpactBucket[];

  topGoodKeys: string[];
  topBadKeys: string[];

  rallyIndexesWhereTeamWon: number[];
  rallyIndexesWhereTeamLost: number[];
};

type ImpactExampleBase = Omit<ImpactExample, "weight" | "importanceReasons">;

function exWithImportance(
  base: ImpactExampleBase,
  imp: { weight: number; reasons: ImportanceReason[] },
): ImpactExample {
  const reasons = imp.reasons;

  return {
    ...base,
    weight: imp.weight,
    importanceReasons: reasons,
  };
}

export type MatchImpactFacts = {
  version: 1;
  packageId: string;

  scoring: "side_out" | "rally";
  finalScore: [number, number];

  players: ImpactFactsPlayer[];
  perTeam: Record<Team01, ImpactFactsTeam>;

  // NEW
  pressureByRallyIndex: Record<number, PressureByRally>;

  meta?: {
    numRallies: number;
    computedFrom: "insights.json";
  };
};

// ---- helpers ----

function otherTeam(t: Team01): Team01 {
  return t === 0 ? 1 : 0;
}

function teamOfPlayerId(playerId: number | null | undefined): Team01 | null {
  if (playerId == null) return null;
  if (playerId === 0 || playerId === 1) return 0;
  if (playerId === 2 || playerId === 3) return 1;
  return null;
}

function clampPlayerId(x: number | null | undefined): 0 | 1 | 2 | 3 | null {
  if (x === 0 || x === 1 || x === 2 || x === 3) return x;
  return null;
}

function serverNumberToPlayerId(serverNumber: number | null | undefined) {
  // 1..4 => players 0..3
  if (serverNumber === 1) return 0;
  if (serverNumber === 2) return 1;
  if (serverNumber === 3) return 2;
  if (serverNumber === 4) return 3;
  return null;
}

function scoreLooksSane(s: unknown): s is [number, number] {
  return (
    Array.isArray(s) &&
    s.length === 2 &&
    typeof s[0] === "number" &&
    typeof s[1] === "number" &&
    Number.isFinite(s[0]) &&
    Number.isFinite(s[1]) &&
    s[0] >= 0 &&
    s[1] >= 0
  );
}

function leadAndTrailFromScore(scoreBefore: [number, number] | null): {
  diff: number | null;
  leadTeam: Team01 | null;
  trailingTeam: Team01 | null;
} {
  if (!scoreBefore) return { diff: null, leadTeam: null, trailingTeam: null };
  const [a, b] = scoreBefore;
  if (a === b) return { diff: 0, leadTeam: null, trailingTeam: null };
  const leadTeam: Team01 = a > b ? 0 : 1;
  return {
    diff: Math.abs(a - b),
    leadTeam,
    trailingTeam: otherTeam(leadTeam),
  };
}

// NEW: importance now also considers lead gaps + droughts (no-point streak)
function computeRallyImportance(args: {
  scoring: "side_out" | "rally";
  serverTeam: Team01 | null;
  scoreBefore: [number, number] | null;

  // NEW pressure inputs
  noPointRunLenBefore: number; // consecutive no-point rallies before this rally
  leadGapRunLen: number; // how long the current lead gap state has persisted
}): { weight: number; reasons: ImportanceReason[] } {
  const {
    scoring,
    serverTeam,
    scoreBefore,
    noPointRunLenBefore,
    leadGapRunLen,
  } = args;

  if (!scoreBefore) return { weight: 1, reasons: ["no_score_context"] };

  const [a, b] = scoreBefore;
  const maxScore = Math.max(a, b);
  const diff = Math.abs(a - b);

  let w = 1;
  const reasons: ImportanceReason[] = [];

  // --- classic clutch ---
  if (maxScore >= 8) {
    w += 1;
    reasons.push("late_game");
  }
  if (diff <= 1) {
    w += 1;
    reasons.push("close_score");
  }
  if (a >= 10 || b >= 10) {
    w += 2;
    reasons.push("game_point");
  }
  if (scoring === "side_out" && serverTeam != null) {
    if (scoreBefore[serverTeam] >= 10) {
      w += 1;
      reasons.push("sideout_server_at_game_point");
    }
  }

  // --- NEW: lead gap pressure ---
  if (diff >= LEAD_GAP_MIN) {
    w += 1;
    reasons.push("lead_gap");
  }
  if (diff >= BIG_LEAD_GAP_MIN) {
    w += 1;
    reasons.push("big_lead_gap");
  }
  if (diff >= LEAD_GAP_MIN && leadGapRunLen >= LEAD_PERSIST_MIN) {
    w += 1;
    reasons.push("lead_has_persisted");
  }

  // --- NEW: drought pressure (no-point runs) ---
  if (noPointRunLenBefore >= DROUGHT_MIN) {
    w += 1;
    reasons.push("score_drought");
  }
  if (noPointRunLenBefore >= BIG_DROUGHT_MIN) {
    w += 1;
    reasons.push("big_score_drought");
  }

  // --- NEW: pressure asymmetry in side-out based on serving role ---
  if (scoring === "side_out" && serverTeam != null) {
    const leadTeam: Team01 | null = a === b ? null : a > b ? 0 : 1;
    const trailingTeam = leadTeam == null ? null : otherTeam(leadTeam);

    // Only apply when someone is actually leading (not tied)
    if (trailingTeam != null && serverTeam === trailingTeam) {
      w += 1;
      reasons.push("trailing_team_serving");

      if (noPointRunLenBefore >= DROUGHT_MIN) {
        w += 1;
        reasons.push("break_drought_opportunity");
      }
    }

    if (leadTeam != null && serverTeam === leadTeam) {
      w += 0; // keep this at 0 for now; reason still useful for UI
      reasons.push("leading_team_serving");
    }
  }
  // Cap keeps things sane for rollups + UI normalization
  return { weight: Math.min(Math.round(w), 8), reasons };
}

function pushExample(arr: ImpactExample[], ex: ImpactExample, max = 6) {
  if (arr.length < max) arr.push(ex);
}

function incBucket(
  m: Map<string, ImpactBucket>,
  key: string,
  ex: ImpactExample,
  by = 1,
) {
  let b = m.get(key);
  if (!b) {
    b = { key, count: 0, examples: [] };
    m.set(key, b);
  }
  b.count += by;
  b.weighted = (b.weighted ?? 0) + (ex.weight ?? 1) * by;
  pushExample(b.examples, ex, 6);
}

function getFinalShot(shots: InsightsShot[] | undefined): {
  shot: InsightsShot | null;
  shotNumber: number | null;
} {
  if (!shots?.length) return { shot: null, shotNumber: null };

  for (let i = shots.length - 1; i >= 0; i--) {
    if (shots[i]?.is_final === true)
      return { shot: shots[i], shotNumber: i + 1 };
  }
  return { shot: shots[shots.length - 1], shotNumber: shots.length };
}

// Extend your InsightsFaults with optional "out" object without breaking anything.
type FaultsPlusOut = InsightsFaults & {
  out?: {
    outcome?: string; // e.g. "landed"
    direction?: string; // e.g. "long" | "wide"
    [k: string]: unknown;
  };
};

function normalizeOutDir(dirRaw: string): string {
  const d = dirRaw.toLowerCase();
  if (d.includes("long") || d.includes("deep")) return "long";
  if (d.includes("left")) return "left";
  if (d.includes("right")) return "right";
  if (d.includes("wide")) return "wide";
  return "unknown";
}

function inferEndKindFromFinalShot(finalShot: InsightsShot | null): {
  endKind: "winner" | "error" | "unknown";
  detail?: string;
  faultType?: string;
  faultSource?: "explicit" | "crossed_net" | "tags";
} {
  if (!finalShot) return { endKind: "unknown" };

  const faults = (finalShot.errors?.faults ?? null) as FaultsPlusOut | null;

  // 1) explicit faults
  if (faults) {
    if (faults.net === true || faults.paddle_hit_net === true) {
      return {
        endKind: "error",
        detail: "errors.faults.net",
        faultType: "net",
        faultSource: "explicit",
      };
    }
    if (faults.kitchen === true) {
      return {
        endKind: "error",
        detail: "errors.faults.kitchen",
        faultType: "kitchen",
        faultSource: "explicit",
      };
    }
    if (faults.out && typeof faults.out === "object") {
      const outcome = faults.out.outcome;
      if (outcome === "landed") {
        const dirRaw =
          typeof faults.out.direction === "string"
            ? faults.out.direction
            : "unknown";

        const dirNorm = normalizeOutDir(dirRaw);
        const rawSuffix =
          dirRaw.toLowerCase() === dirNorm ? "" : ` (raw=${dirRaw})`;

        return {
          endKind: "error",
          detail: `errors.faults.out.landed:${dirNorm}${rawSuffix}`,
          faultType: `out:${dirNorm}`,
          faultSource: "explicit",
        };
      }
      return {
        endKind: "error",
        detail: "errors.faults.out",
        faultType: "out:unknown",
        faultSource: "explicit",
      };
    }
  }

  // 2) crossed_net fallback
  const crossed = finalShot.resulting_ball_movement?.crossed_net;
  if (crossed === false) {
    return {
      endKind: "error",
      detail: "resulting_ball_movement.crossed_net=false",
      faultType: "net",
      faultSource: "crossed_net",
    };
  }

  // 3) winner
  if (
    typeof finalShot.winner_type === "string" &&
    finalShot.winner_type.length
  ) {
    return {
      endKind: "winner",
      detail: `winner_type:${finalShot.winner_type}`,
    };
  }

  // 4) tags heuristic
  const tags = finalShot.tags;
  if (tags && typeof tags === "object") {
    const joined = Object.keys(tags).join("|").toLowerCase();
    if (
      joined.includes("fault") ||
      joined.includes("out") ||
      joined.includes("net")
    ) {
      return {
        endKind: "error",
        detail: "tags:fault/out/net",
        faultType: "unknown",
        faultSource: "tags",
      };
    }
  }

  return { endKind: "unknown" };
}

function faultDetailOnly(finalShot: InsightsShot | null): string | null {
  const faults = (finalShot?.errors?.faults ?? null) as FaultsPlusOut | null;
  if (!faults) return null;

  if (faults.net === true || faults.paddle_hit_net === true)
    return "errors.faults.net";
  if (faults.kitchen === true) return "errors.faults.kitchen";
  if (faults.out && typeof faults.out === "object") {
    const outcome = faults.out.outcome;
    const dir =
      typeof faults.out.direction === "string"
        ? faults.out.direction
        : "unknown";
    if (outcome === "landed") {
      const dirNorm = normalizeOutDir(dir);
      const rawSuffix = dir.toLowerCase() === dirNorm ? "" : ` (raw=${dir})`;
      return `errors.faults.out.landed:${dirNorm}${rawSuffix}`;
    }
    return "errors.faults.out";
  }
  return null;
}

// Sort helpers:
// - By default, show "important" first (weighted), tie-break by count.
function sortedBucketsByWeighted(m: Map<string, ImpactBucket>) {
  return Array.from(m.values()).sort((a, b) => {
    const aw = a.weighted ?? a.count;
    const bw = b.weighted ?? b.count;
    if (bw !== aw) return bw - aw;
    return b.count - a.count;
  });
}

function mergeBucketsInto(
  target: Map<string, ImpactBucket>,
  sourceBuckets: ImpactBucket[],
  opts?: { maxExamplesPerBucket?: number },
) {
  const maxEx = opts?.maxExamplesPerBucket ?? 6;

  for (const sb of sourceBuckets) {
    let tb = target.get(sb.key);
    if (!tb) {
      tb = { key: sb.key, count: 0, examples: [], weighted: 0 };
      target.set(sb.key, tb);
    }
    tb.count += sb.count;
    tb.weighted = (tb.weighted ?? 0) + (sb.weighted ?? sb.count);

    for (const ex of sb.examples) {
      if (tb.examples.length >= maxEx) break;
      tb.examples.push(ex);
    }
  }
}

function uniqSorted(nums: number[]) {
  const s = new Set<number>();
  for (const n of nums) s.add(n);
  return Array.from(s).sort((a, b) => a - b);
}

// ---- main builder ----

function pointAwardedFromNextScore(args: {
  scoreBefore: [number, number] | null;
  nextScoreBefore: [number, number] | null;
}): { pointScored: boolean; pointAwardedTeam: Team01 | null } {
  const { scoreBefore, nextScoreBefore } = args;
  if (!scoreBefore || !nextScoreBefore)
    return { pointScored: false, pointAwardedTeam: null };

  const [a0, b0] = scoreBefore;
  const [a1, b1] = nextScoreBefore;

  if (a1 === a0 && b1 === b0)
    return { pointScored: false, pointAwardedTeam: null };

  // Common expected case: exactly one team increments by 1
  if (a1 === a0 + 1 && b1 === b0)
    return { pointScored: true, pointAwardedTeam: 0 };
  if (b1 === b0 + 1 && a1 === a0)
    return { pointScored: true, pointAwardedTeam: 1 };

  // Unexpected jump (still means “a point happened”, but don’t guess team)
  return { pointScored: true, pointAwardedTeam: null };
}

export function buildMatchImpactFactsFromInsights(args: {
  packageId: string;
  insights: InsightsJson;
}): MatchImpactFacts {
  const { packageId, insights } = args;

  const scoringRaw = insights.game_data?.scoring;
  const scoring: "side_out" | "rally" =
    scoringRaw === "rally" ? "rally" : "side_out";

  const rallies = insights.rallies ?? [];

  const goodByPlayer: Record<0 | 1 | 2 | 3, Map<string, ImpactBucket>> = {
    0: new Map(),
    1: new Map(),
    2: new Map(),
    3: new Map(),
  };
  const badByPlayer: Record<0 | 1 | 2 | 3, Map<string, ImpactBucket>> = {
    0: new Map(),
    1: new Map(),
    2: new Map(),
    3: new Map(),
  };

  const rallyIndexesWhereTeamWon: Record<Team01, number[]> = { 0: [], 1: [] };
  const rallyIndexesWhereTeamLost: Record<Team01, number[]> = { 0: [], 1: [] };

  // For final score
  let lastRunningScore: [number, number] | null = null;
  let lastRallyWinnerTeam: Team01 | null = null;
  let lastRallyServerTeam: Team01 | null = null;

  // per-team rollups
  const teamGood: Record<Team01, Map<string, ImpactBucket>> = {
    0: new Map(),
    1: new Map(),
  };
  const teamBad: Record<Team01, Map<string, ImpactBucket>> = {
    0: new Map(),
    1: new Map(),
  };

  // NEW: drought + lead persistence trackers
  let droughtByTeam: [number, number] = [0, 0]; // rallies since each team last scored a point

  let noPointRunLen = 0; // consecutive rallies where score does NOT change
  let prevLeadTeam: Team01 | null = null;
  let leadGapRunLen = 0; // consecutive rallies w/ same leadTeam and diff >= LEAD_GAP_MIN

  const pressureByRallyIndex: Record<number, PressureByRally> = {};

  // rally loop: compute per-rally context, attribute good/bad events, and track pressure
  for (let rallyIndex = 0; rallyIndex < rallies.length; rallyIndex++) {
    const r: InsightsRally = rallies[rallyIndex];

    const winnerTeam: Team01 | null =
      r.winning_team === 0 || r.winning_team === 1 ? r.winning_team : null;

    // if (winnerTeam != null) {
    //   rallyIndexesWhereTeamWon[winnerTeam].push(rallyIndex);
    //   rallyIndexesWhereTeamLost[otherTeam(winnerTeam)].push(rallyIndex);
    // }

    //     if (winnerTeamForAttribution != null) {
    //   rallyIndexesWhereTeamWon[winnerTeamForAttribution].push(rallyIndex);
    //   rallyIndexesWhereTeamLost[otherTeam(winnerTeamForAttribution)].push(rallyIndex);
    // }

    const serverPlayerId = serverNumberToPlayerId(
      r.scoring_info?.server_number,
    );
    const serverTeam = teamOfPlayerId(serverPlayerId);

    const rs = r.scoring_info?.running_score;
    const scoreBefore: [number, number] | null = scoreLooksSane(rs)
      ? [rs[0], rs[1]]
      : null;

    const nextRs = rallies[rallyIndex + 1]?.scoring_info?.running_score;
    const nextScoreBefore: [number, number] | null = scoreLooksSane(nextRs)
      ? [nextRs[0], nextRs[1]]
      : null;

    const shots = r.shots ?? [];
    const { shot: finalShot, shotNumber: finalShotNumber } =
      getFinalShot(shots);

    const finalHitter = clampPlayerId(finalShot?.player_id ?? null);
    const finalHitterTeam = teamOfPlayerId(finalHitter);

    const endInf = inferEndKindFromFinalShot(finalShot);

    // canonical attribution winner
    const winnerTeamForAttribution: Team01 | null =
      endInf.endKind === "winner" && finalHitterTeam != null
        ? finalHitterTeam
        : winnerTeam;
    // const scoreBefore: [number, number] | null = scoreLooksSane(
    //   r.scoring_info?.running_score,
    // )
    //   ? [r.scoring_info!.running_score[0], r.scoring_info!.running_score[1]]
    //   : null;

    // ********
    const fromDelta = pointAwardedFromNextScore({
      scoreBefore,
      nextScoreBefore,
    });

    // Start with "truth" when nextScoreBefore exists
    let pointScored = fromDelta.pointScored;
    let pointAwardedTeam: Team01 | null = fromDelta.pointAwardedTeam;

    // Last rally fallback ONLY when no nextScoreBefore
    if (!nextScoreBefore) {
      if (winnerTeamForAttribution != null) {
        if (scoring === "rally") {
          pointScored = true;
          pointAwardedTeam = winnerTeamForAttribution;
        } else {
          // side_out: point only if server won
          if (serverTeam != null && serverTeam === winnerTeamForAttribution) {
            pointScored = true;
            pointAwardedTeam = serverTeam;
          } else {
            pointScored = false;
            pointAwardedTeam = null;
          }
        }
      } else {
        pointScored = false;
        pointAwardedTeam = null;
      }
    }

    // ********

    if (scoreBefore) lastRunningScore = [scoreBefore[0], scoreBefore[1]];

    // ---- last rally fallback (only when there is no next score to diff against) ----
    if (!nextScoreBefore) {
      // Determine if a point was scored on the last rally
      if (winnerTeamForAttribution != null) {
        if (scoring === "rally") {
          pointScored = true;
          pointAwardedTeam = winnerTeamForAttribution;
        } else {
          // side_out: point only if server won
          if (serverTeam != null && serverTeam === winnerTeamForAttribution) {
            pointScored = true;
            pointAwardedTeam = serverTeam;
          } else {
            pointScored = false;
            pointAwardedTeam = null;
          }
        }
      } else {
        pointScored = false;
        pointAwardedTeam = null;
      }
    }

    const droughtByTeamBefore: [number, number] = [
      droughtByTeam[0],
      droughtByTeam[1],
    ];
    const noPointRunLenBefore = noPointRunLen;
    const maxDroughtBefore = Math.max(
      droughtByTeamBefore[0],
      droughtByTeamBefore[1],
    );
    const droughtTeamBefore: Team01 | null =
      droughtByTeamBefore[0] > droughtByTeamBefore[1]
        ? 0
        : droughtByTeamBefore[1] > droughtByTeamBefore[0]
          ? 1
          : null;

    const { diff, leadTeam, trailingTeam } = leadAndTrailFromScore(scoreBefore);

    // lead gap run len is also a “before rally” concept (based on scoreBefore)
    const isLeadGapState =
      diff != null && diff >= LEAD_GAP_MIN && leadTeam != null;
    if (isLeadGapState) {
      if (prevLeadTeam === leadTeam) leadGapRunLen += 1;
      else leadGapRunLen = 1;
      prevLeadTeam = leadTeam;
    } else {
      leadGapRunLen = 0;
      prevLeadTeam = null;
    }
    const leadGapRunLenBefore = leadGapRunLen;

    const rallyImportance = computeRallyImportance({
      scoring,
      serverTeam,
      scoreBefore,
      noPointRunLenBefore,
      leadGapRunLen: leadGapRunLenBefore,
    });

    // consecutive rallies where score does NOT change
    if (pointScored) noPointRunLen = 0;
    else noPointRunLen += 1;

    // TEAM point droughts: "rallies since each team last scored a point"
    if (pointAwardedTeam === 0) {
      droughtByTeam = [0, droughtByTeam[1] + 1];
    } else if (pointAwardedTeam === 1) {
      droughtByTeam = [droughtByTeam[0] + 1, 0];
    } else {
      droughtByTeam = [droughtByTeam[0] + 1, droughtByTeam[1] + 1];
    }

    // const nextRs = rallies[rallyIndex + 1]?.scoring_info?.running_score;
    // const nextScoreBefore: [number, number] | null = scoreLooksSane(nextRs)
    //   ? [nextRs[0], nextRs[1]]
    //   : null;

    // const fromDelta = pointAwardedFromNextScore({
    //   scoreBefore,
    //   nextScoreBefore,
    // });

    // Update lead gap run length (based on scoreBefore state)
    if (isLeadGapState) {
      if (prevLeadTeam === leadTeam) leadGapRunLen += 1;
      else leadGapRunLen = 1;
      prevLeadTeam = leadTeam;
    } else {
      leadGapRunLen = 0;
      prevLeadTeam = null;
    }

    const winnerTeamSource: WinnerSource | undefined =
      winnerTeamForAttribution == null
        ? undefined
        : winnerTeamForAttribution !== winnerTeam &&
            endInf.endKind === "winner" &&
            finalHitterTeam != null
          ? "finalShot.playerTeam"
          : "rally.winning_team";

    const winnerTeamEffective = winnerTeamForAttribution; // canonical

    lastRallyWinnerTeam = winnerTeamEffective;
    lastRallyServerTeam = serverTeam;

    if (winnerTeamForAttribution != null) {
      rallyIndexesWhereTeamWon[winnerTeamForAttribution].push(rallyIndex);
      rallyIndexesWhereTeamLost[otherTeam(winnerTeamForAttribution)].push(
        rallyIndex,
      );
    }

    const endedOnReturnShot2 =
      winnerTeamForAttribution != null &&
      shots.length === 2 &&
      finalShotNumber === 2;

    // Build ctx ONCE per rally AFTER all the above exist
    const droughtTeam: 0 | 1 | null =
      droughtByTeam[0] > droughtByTeam[1]
        ? 0
        : droughtByTeam[1] > droughtByTeam[0]
          ? 1
          : null;

    pressureByRallyIndex[rallyIndex] = {
      rallyIndex,
      scoreBefore: scoreBefore ?? undefined,
      scoreDiff: diff ?? null,
      leadTeam,
      trailingTeam,

      leadGapRunLen: leadGapRunLenBefore,

      noPointRunLenBefore: maxDroughtBefore,
      droughtTeam: droughtTeamBefore,
      droughtByTeam: droughtByTeamBefore,
      droughtByTeamBefore,

      weight: rallyImportance.weight,
      reasons: rallyImportance.reasons,
    };

    const ctx: ImpactExampleBase = {
      rallyIndex,
      scoreBefore: scoreBefore ?? undefined,

      // NEW pressure context for UI
      scoreDiff: diff ?? undefined,
      leadTeam,
      trailingTeam,
      noPointRunLenBefore,
      leadGapRunLen,

      winnerTeam,
      winnerTeamCanonical: winnerTeamForAttribution ?? undefined,
      winnerTeamSource,
      serverPlayerId,
      serverTeam,

      finalHitterPlayerId: finalHitter,
      finalHitterTeam,

      shotsInRally: shots.length,

      endKind: endInf.endKind,
      faultType: endInf.faultType ?? null,
      faultSource: endInf.faultSource ?? null,
      endedOnReturnShot2,
    };

    // GOOD: winner_type + putaway
    if (finalHitter != null && finalShot) {
      const teamsAlign =
        winnerTeamForAttribution == null ||
        finalHitterTeam == null ||
        winnerTeamForAttribution === finalHitterTeam;

      if (teamsAlign) {
        if (
          typeof finalShot.winner_type === "string" &&
          finalShot.winner_type.length
        ) {
          const k =
            finalShot.winner_type === "clean"
              ? "good.cleanWinner"
              : `good.winnerType.${finalShot.winner_type}`;

          incBucket(
            goodByPlayer[finalHitter],
            k,
            exWithImportance(
              {
                ...ctx,
                shotNumber: finalShotNumber,
                note: `winner_type=${finalShot.winner_type}`,
              },
              rallyImportance,
            ),
          );
        }

        if (finalShot.is_putaway === true) {
          incBucket(
            goodByPlayer[finalHitter],
            "good.putaway",
            exWithImportance(
              { ...ctx, shotNumber: finalShotNumber, note: "is_putaway" },
              rallyImportance,
            ),
          );
        }
      }
    }
    // BAD: explicit faults or fallback
    if (
      winnerTeamForAttribution != null &&
      finalHitter != null &&
      finalHitterTeam != null
    ) {
      const losingTeam = otherTeam(winnerTeamForAttribution);

      if (endInf.endKind === "error") {
        const key =
          endInf.faultType && endInf.faultType !== "unknown"
            ? `bad.fault.${endInf.faultType}`
            : "bad.fault.unknown";

        incBucket(
          badByPlayer[finalHitter],
          key,
          exWithImportance(
            {
              ...ctx,
              shotNumber: finalShotNumber,
              note: endInf.detail ?? "ended on error/fault",
            },
            rallyImportance,
          ),
        );
      } else if (
        endInf.endKind === "unknown" &&
        finalHitterTeam === losingTeam
      ) {
        incBucket(
          badByPlayer[finalHitter],
          "bad.lostRallyOnOwnShot",
          exWithImportance(
            {
              ...ctx,
              shotNumber: finalShotNumber,
              note: "final hitter on losing team (fallback)",
            },
            rallyImportance,
          ),
        );
      }
    }

    // Return ended rally early (shot 2) — weight + context
    if (
      winnerTeamEffective != null &&
      finalHitter != null &&
      shots.length === 2 &&
      finalShotNumber === 2
    ) {
      const losingTeam = otherTeam(winnerTeamEffective);
      const returnerTeam = teamOfPlayerId(finalHitter);

      const isWinnerOnReturn =
        typeof finalShot?.winner_type === "string" &&
        finalShot.winner_type.length > 0;

      const hasExplicitFault =
        endInf.endKind === "error" && endInf.faultSource === "explicit";

      if (
        !isWinnerOnReturn &&
        returnerTeam === losingTeam &&
        !hasExplicitFault
      ) {
        const note =
          faultDetailOnly(finalShot) ??
          (finalShot?.resulting_ball_movement?.crossed_net === false
            ? "resulting_ball_movement.crossed_net=false"
            : "ended on return (shot 2)");

        incBucket(
          badByPlayer[finalHitter],
          "bad.rallyEndedOnReturn",
          exWithImportance(
            {
              ...ctx,
              shotNumber: 2,
              note,
              endedOnReturnShot2: true,
            },
            rallyImportance,
          ),
        );
      }
    }

    // Team server win/loss attribution (weighted)
    if (serverTeam != null && winnerTeamEffective != null) {
      if (serverTeam === winnerTeamEffective) {
        incBucket(
          teamGood[serverTeam],
          "team.serverWonRally",
          exWithImportance(
            {
              ...ctx,
              shotNumber: 1,
            },
            rallyImportance,
          ),
        );
      } else {
        incBucket(
          teamBad[serverTeam],
          "team.serverLostRally",
          exWithImportance(
            {
              ...ctx,
              shotNumber: 1,
            },
            rallyImportance,
          ),
        );
      }
    }

    // no-point run (score did not change)
    if (pointScored) noPointRunLen = 0;
    else noPointRunLen += 1;

    // update team droughts
    if (pointAwardedTeam === 0) {
      droughtByTeam = [0, droughtByTeam[1] + 1];
    } else if (pointAwardedTeam === 1) {
      droughtByTeam = [droughtByTeam[0] + 1, 0];
    } else {
      droughtByTeam = [droughtByTeam[0] + 1, droughtByTeam[1] + 1];
    }
  } // rally loop

  // Final score computation
  let finalScore: [number, number] = [0, 0];

  if (
    lastRunningScore &&
    lastRallyWinnerTeam != null &&
    lastRallyServerTeam != null
  ) {
    finalScore = [lastRunningScore[0], lastRunningScore[1]];
    if (scoring === "side_out") {
      if (lastRallyServerTeam === lastRallyWinnerTeam) {
        finalScore[lastRallyWinnerTeam] += 1;
      }
    } else {
      finalScore[lastRallyWinnerTeam] += 1;
    }
  } else {
    // fallback compute from scratch
    let a = 0,
      b = 0;
    for (let rallyIndex = 0; rallyIndex < rallies.length; rallyIndex++) {
      const r = rallies[rallyIndex];
      const wt: Team01 | null =
        r.winning_team === 0 || r.winning_team === 1 ? r.winning_team : null;
      if (wt == null) continue;

      const serverPlayerId = serverNumberToPlayerId(
        r.scoring_info?.server_number,
      );
      //   const serverTeam = teamOfPlayerId(serverPlayerId);
      const serverTeam =
        teamOfPlayerId(serverPlayerId) ??
        ((r as any)?.serving_team === 0 || (r as any)?.serving_team === 1
          ? (r as any).serving_team
          : null);

      if (scoring === "side_out") {
        if (serverTeam != null && serverTeam === wt) {
          if (wt === 0) a++;
          else b++;
        }
      } else {
        if (wt === 0) a++;
        else b++;
      }
    }
    finalScore = [a, b];
  }

  const players: ImpactFactsPlayer[] = ([0, 1, 2, 3] as const).map(
    (playerId) => ({
      playerId,
      team: teamOfPlayerId(playerId) as Team01,
      good: sortedBucketsByWeighted(goodByPlayer[playerId]),
      bad: sortedBucketsByWeighted(badByPlayer[playerId]),
    }),
  );

  // merge player buckets into team maps (preserves weighted now)
  for (const p of players) {
    mergeBucketsInto(teamGood[p.team], p.good, { maxExamplesPerBucket: 8 });
    mergeBucketsInto(teamBad[p.team], p.bad, { maxExamplesPerBucket: 8 });
  }

  // Sort team buckets by importance too
  const team0Good = sortedBucketsByWeighted(teamGood[0]);
  const team0Bad = sortedBucketsByWeighted(teamBad[0]);
  const team1Good = sortedBucketsByWeighted(teamGood[1]);
  const team1Bad = sortedBucketsByWeighted(teamBad[1]);

  const perTeam: Record<Team01, ImpactFactsTeam> = {
    0: {
      team: 0,
      good: team0Good,
      bad: team0Bad,
      topGoodKeys: team0Good.slice(0, 6).map((b) => b.key),
      topBadKeys: team0Bad.slice(0, 6).map((b) => b.key),
      rallyIndexesWhereTeamWon: uniqSorted(rallyIndexesWhereTeamWon[0]),
      rallyIndexesWhereTeamLost: uniqSorted(rallyIndexesWhereTeamLost[0]),
    },
    1: {
      team: 1,
      good: team1Good,
      bad: team1Bad,
      topGoodKeys: team1Good.slice(0, 6).map((b) => b.key),
      topBadKeys: team1Bad.slice(0, 6).map((b) => b.key),
      rallyIndexesWhereTeamWon: uniqSorted(rallyIndexesWhereTeamWon[1]),
      rallyIndexesWhereTeamLost: uniqSorted(rallyIndexesWhereTeamLost[1]),
    },
  };

  return {
    version: 1,
    packageId,
    scoring,
    finalScore,
    pressureByRallyIndex,
    players,
    perTeam,
    meta: { numRallies: rallies.length, computedFrom: "insights.json" },
  };
}
